<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading bed/ward assignment...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="init">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-bed"></i> Bed / Ward Assignment</h3>
        </div>
        <div class="card-body">
          <div v-if="currentAdmission" class="current-admission">
            <div class="admission-row">
              <span><strong>Ward:</strong> {{ currentAdmission.ward?.ward_name || "N/A" }}</span>
              <span><strong>Bed:</strong> {{ currentAdmission.bed?.bed_number || "Not yet assigned" }}</span>
              <span
                ><strong>Admitted:</strong>
                {{ formatDateTime(currentAdmission.admission?.admission_date) }}</span
              >
            </div>
            <p v-if="currentAdmission.admission?.reason" class="admission-reason">
              <strong>Reason:</strong> {{ currentAdmission.admission.reason }}
            </p>
            <p v-if="currentAdmission.admission?.notes" class="admission-reason">
              <strong>Notes:</strong> {{ currentAdmission.admission.notes }}
            </p>
          </div>

          <div v-else class="no-data">
            This patient has not yet been admitted to a ward for this visit.
          </div>

          <div v-if="!currentAdmission" class="assignment-form">
            <h4>Admit to Ward</h4>
            <form @submit.prevent="submitAdmission">
              <div class="form-grid">
                <div class="form-group">
                  <label>Admission Date/Time *</label>
                  <input type="datetime-local" v-model="form.admission_date" required class="form-control" />
                </div>
                <div class="form-group">
                  <label>Ward *</label>
                  <select v-model="form.ward" required class="form-control" @change="onWardChange">
                    <option value="">Select Ward</option>
                    <option v-for="ward in wards" :key="ward.id" :value="ward.id">
                      {{ ward.ward_name }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Bed</label>
                  <select v-model="form.bed_id" class="form-control">
                    <option value="">Select Bed (optional)</option>
                    <option v-for="bed in availableBeds" :key="bed.id" :value="bed.id">
                      {{ bed.bed_number }}{{ bed.is_occupied ? " (Occupied)" : "" }}
                    </option>
                  </select>
                </div>
                <div class="form-group full-width">
                  <label>Reason for Admission</label>
                  <input type="text" v-model="form.reason" class="form-control" />
                </div>
                <div class="form-group full-width">
                  <label>Notes</label>
                  <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
                </div>
              </div>
              <button type="submit" class="btn-submit" :disabled="submitting">
                {{ submitting ? "Admitting..." : "Admit to Ward" }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "MaternityBedAssignment",
  components: { full_head },
  data() {
    return {
      loading: false,
      submitting: false,
      error: null,
      visit_id: null,
      patient_id: null,
      currentAdmission: null,
      wards: [],
      beds: [],
      form: {
        admission_date: this.toLocalDateTime(new Date()),
        ward: "",
        bed_id: "",
        reason: "",
        notes: "",
      },
    };
  },
  computed: {
    availableBeds() {
      if (!this.form.ward) return [];
      return this.beds.filter((bed) => {
        const wardId = bed.ward_id ?? bed["ward_id "];
        return String(wardId) === String(this.form.ward);
      });
    },
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.init();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    toLocalDateTime(value) {
      const d = new Date(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    async init() {
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([
          this.fetchPatient(),
          this.fetchCurrentAdmission(),
          this.fetchWards(),
          this.fetchBeds(),
        ]);
      } catch (err) {
        console.error("Error initializing bed/ward assignment:", err);
        this.error = "An error occurred while loading this page. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async fetchPatient() {
      const response = await generalApiRequest(
        `get_patient_object_from_visit/${this.visit_id}`,
        "GET",
        {},
        1
      );
      this.patient_id = response?.data?.id || response?.data?.patient_id || null;
    },

    async fetchCurrentAdmission() {
      const response = await generalApiRequest(
        `inpatient/admission/visit/${this.visit_id}`,
        "GET",
        {},
        1
      );
      if (response.success) {
        this.currentAdmission = response.data;
      }
    },

    async fetchWards() {
      const response = await generalApiRequest("inpatient/getCompanyWards", "GET", {}, 1);
      this.wards = Array.isArray(response) ? response : response?.data || [];
    },

    async fetchBeds() {
      const response = await generalApiRequest("getCompanyBeds/all", "GET", {}, 1);
      this.beds = Array.isArray(response) ? response : response?.data || [];
    },

    onWardChange() {
      this.form.bed_id = "";
    },

    async submitAdmission() {
      this.submitting = true;
      try {
        const payload = {
          visit_id: this.visit_id,
          patient_id: this.patient_id,
          admission_date: this.form.admission_date,
          ward: this.form.ward,
          bed_id: this.form.bed_id || null,
          reason: this.form.reason,
          notes: this.form.notes,
        };

        const response = await generalApiRequest("inpatients/request_admission", "POST", payload, 1);

        if (response.success) {
          await this.fetchCurrentAdmission();
        } else {
          alert(response.message || "Failed to admit patient to ward");
        }
      } catch (error) {
        console.error("Error admitting patient:", error);
        alert("An error occurred while admitting the patient. Please try again.");
      } finally {
        this.submitting = false;
      }
    },

    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #38a169; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #38a169; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #38a169; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; margin-bottom: 16px; }
.current-admission { background: #f0fff4; border: 1px solid #9ae6b4; border-radius: 8px; padding: 14px; margin-bottom: 16px; }
.admission-row { display: flex; flex-wrap: wrap; gap: 20px; font-size: 14px; color: #22543d; margin-bottom: 8px; }
.admission-reason { font-size: 13px; color: #276749; margin: 4px 0; }
.assignment-form h4 { font-size: 14px; color: #495057; margin-bottom: 12px; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 16px; }
.form-group { margin-bottom: 10px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; font-size: 13px; }
.form-control { width: 100%; padding: 9px; border: 1px solid #ddd; border-radius: 5px; font-size: 13px; }
.btn-submit { background: linear-gradient(135deg, #48bb78 0%, #38a169 100%); color: white; border: none; padding: 10px 28px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
